import { useCurrentOrg, useCurrentUser, useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { JurisdictionIcon, NewAnalysisIcon, RimsIcon } from '../components/BrandIcons';
import type { OnboardingStatus } from '../components/OnboardingChecklist';
import { Surface } from '../components/Surface';
import { useFeature } from '../lib/features';
import { hasSeenTour, startTour } from '../lib/tour';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/** Shape of a jurisdiction-profile row, only the fields the dashboard uses. */
type JurisdictionProfile = {
  id: string;
  name: string;
  cityName?: string | null;
  isDefault: boolean;
};

/** Shape of a rims-dataset row, only the fields the dashboard uses. */
type RimsDataset = {
  id: string;
  status: 'uploaded' | 'parsing' | 'ready' | 'failed';
};

export function DashboardRoute() {
  const user = useCurrentUser();
  const org = useCurrentOrg();
  const getToken = useSessionTokenGetter();
  const intakeEnabled = useFeature('intake_forms');
  const [analysisCount, setAnalysisCount] = useState<number | null>(null);
  const [pendingSubmissions, setPendingSubmissions] = useState<number | null>(null);
  const [defaultProfile, setDefaultProfile] = useState<JurisdictionProfile | null>(null);
  const [rimsStatus, setRimsStatus] = useState<
    'missing' | 'verified' | 'pending' | 'failed' | null
  >(null);
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [analysesRes, pendingRes, profilesRes, rimsRes, onboardingRes] = await Promise.all([
          fetch(`${API}/api/v1/analyses`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/v1/intake-submissions/pending-count`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/v1/jurisdiction-profiles`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/v1/rims-datasets`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/v1/onboarding-status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (cancelled) return;
        if (analysesRes.ok) {
          const data = (await analysesRes.json()) as { count: number };
          if (!cancelled) setAnalysisCount(data.count);
        }
        if (pendingRes.ok) {
          const data = (await pendingRes.json()) as { count: number };
          if (!cancelled) setPendingSubmissions(data.count);
        }
        if (profilesRes.ok) {
          const data = (await profilesRes.json()) as { profiles: JurisdictionProfile[] };
          // Prefer the isDefault profile; if none is marked default but at least one
          // exists, show the first one as the working profile.
          const def = data.profiles.find((p) => p.isDefault) ?? data.profiles[0] ?? null;
          if (!cancelled) setDefaultProfile(def);
        }
        if (rimsRes.ok) {
          const data = (await rimsRes.json()) as { datasets: RimsDataset[] };
          if (!cancelled) {
            if (data.datasets.length === 0) {
              setRimsStatus('missing');
            } else if (data.datasets.some((d) => d.status === 'ready')) {
              setRimsStatus('verified');
            } else if (data.datasets.some((d) => d.status === 'failed')) {
              setRimsStatus('failed');
            } else {
              setRimsStatus('pending');
            }
          }
        } else if (!cancelled) {
          setRimsStatus('missing');
        }
        if (onboardingRes.ok) {
          const data = (await onboardingRes.json()) as OnboardingStatus;
          if (!cancelled) setOnboarding(data);
        }
      } catch {
        // non-fatal — dashboard still renders without the counts
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // First-run guided tour: auto-launch once the user has finished initial
  // setup and is actually on the dashboard. A short delay lets the quick-link
  // cards and sidebar render so the coachmarks have something to point at.
  useEffect(() => {
    if (!onboarding || onboarding.needsInitialSetup || hasSeenTour()) return;
    const t = setTimeout(() => startTour(), 600);
    return () => clearTimeout(t);
  }, [onboarding]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Dashboard</h2>
          <p className="mt-1 text-sm text-slate-500">
            Welcome{user?.fullName ? `, ${user.fullName}` : ''}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => startTour()}
          className="shrink-0 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          Take a tour
        </button>
      </div>

      {onboarding?.needsInitialSetup && (
        <div className="flex items-start gap-3 rounded-lg border border-brand-teal-300 bg-brand-teal-50 px-4 py-3 text-sm text-brand-teal-900">
          <span className="mt-0.5 shrink-0 text-brand-teal-600">→</span>
          <div className="flex-1">
            <span className="font-semibold">Finish setting up your organization.</span> Tell us your
            city + state and we'll preload your jurisdiction so you can run an analysis in a couple
            of minutes.{' '}
            <Link to="/onboarding" className="font-medium underline">
              Run the 30-second setup
            </Link>
            .
          </div>
        </div>
      )}

      {onboarding?.usesSampleData && !onboarding.needsInitialSetup && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="mt-0.5 shrink-0 text-amber-500">⚠</span>
          <div className="flex-1">
            <span className="font-semibold">You're using estimated data.</span> Your jurisdiction
            uses state-level estimated tax + cost rates and the national RIMS II fallback.{' '}
            <Link to="/settings/jurisdiction" className="font-medium underline">
              Refine your jurisdiction
            </Link>{' '}
            or{' '}
            <Link to="/settings/methodology" className="font-medium underline">
              methodology
            </Link>{' '}
            before sharing any analysis externally.
          </div>
        </div>
      )}

      {/* Only nudge orgs that can actually use methodology governance (Enterprise)
          and have finished initial setup. Trial/Professional orgs aren't entitled
          to lock a methodology, so the prompt would just be noise for them. */}
      {onboarding?.governanceAvailable &&
        !onboarding.governanceEnabled &&
        !onboarding.needsInitialSetup && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span className="mt-0.5 shrink-0 text-amber-500">⚠</span>
            <div>
              <span className="font-semibold">Methodology not locked.</span> Each analysis uses the
              values you enter for that project. To apply one consistent set of assumptions across
              every analysis, set up a methodology profile under Organization → Methodology.
            </div>
          </div>
        )}

      <div className="grid gap-4 md:grid-cols-3">
        <Surface>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
            Active Organization
          </div>
          <div className="mt-2 font-display text-2xl leading-tight text-slate-900">
            {org?.name ?? 'No organization'}
          </div>
          {org?.role && <div className="mt-1 text-xs text-slate-500">Your role: {org.role}</div>}
        </Surface>

        <Surface>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
            Analyses
          </div>
          <div className="mt-2 font-display text-3xl leading-tight tabular-nums text-slate-900">
            {analysisCount === null ? '—' : analysisCount}
          </div>
          {analysisCount !== null && analysisCount > 0 && (
            <Link to="/analyses" className="mt-1 block text-xs text-brand-teal-600 hover:underline">
              View all →
            </Link>
          )}
          {analysisCount === 0 && (
            <Link to="/analyses/new" className="mt-1 block text-xs text-slate-500 hover:underline">
              + Start your first analysis
            </Link>
          )}
        </Surface>

        {intakeEnabled && (
          <Surface>
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
              Pending Submissions
            </div>
            <div className="mt-2 font-display text-3xl leading-tight tabular-nums text-slate-900">
              {pendingSubmissions === null ? '—' : pendingSubmissions}
            </div>
            {pendingSubmissions !== null && pendingSubmissions > 0 && (
              <Link to="/forms" className="mt-1 block text-xs text-brand-teal-600 hover:underline">
                Review submissions →
              </Link>
            )}
            {pendingSubmissions === 0 && (
              <Link to="/forms" className="mt-1 block text-xs text-slate-500 hover:underline">
                Manage intake forms
              </Link>
            )}
          </Surface>
        )}
      </div>

      {/* Quick links — brand-styled SVG icons to match the logo. */}
      <div className="grid gap-3 md:grid-cols-3">
        <Link to="/analyses/new" className="block" data-tour="new-analysis">
          <Surface interactive className="flex items-center gap-3">
            <NewAnalysisIcon size={40} className="shrink-0" />
            <div>
              <div className="text-sm font-medium text-slate-900">New Analysis</div>
              <div className="text-xs text-slate-500">Run the 6-step wizard</div>
            </div>
          </Surface>
        </Link>

        <Link to="/settings/jurisdiction" className="block" data-tour="jurisdiction">
          <Surface interactive className="flex items-center gap-3">
            <JurisdictionIcon size={40} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">Jurisdiction Profile</div>
              <div className="truncate text-xs text-slate-500">
                {defaultProfile === null ? 'No profile yet — tap to create' : defaultProfile.name}
              </div>
            </div>
          </Surface>
        </Link>

        <Link to="/settings/rims" className="block">
          <Surface interactive className="flex items-center gap-3">
            <RimsIcon size={40} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">RIMS Multipliers</div>
              <div className="truncate text-xs">
                {rimsStatus === null && <span className="text-slate-500">Loading…</span>}
                {rimsStatus === 'missing' && (
                  <span className="font-medium text-develio-danger">Data missing</span>
                )}
                {rimsStatus === 'pending' && (
                  <span className="text-amber-600">Upload in progress</span>
                )}
                {rimsStatus === 'failed' && (
                  <span className="font-medium text-develio-danger">Parse failed</span>
                )}
                {rimsStatus === 'verified' && (
                  <span className="text-brand-green-700">Uploaded &amp; verified ✓</span>
                )}
              </div>
            </div>
          </Surface>
        </Link>
      </div>
    </div>
  );
}
